import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  HStack,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { notificationsService } from "services/notificationsService";
import { useCustomToast } from "components/CustomToast/CustomToast";

const ConfirmDialogWithFormModal = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const showToast = useCustomToast();
  const [title, setTitle] = React.useState("");
  const dialogRef = useRef(undefined);
  const [issueDescription, setIssueDescription] = useState("");
  const [responsibleUser, setResponsibleUser] = useState("");
  const [useIssueDescription, setUseIssueDescription] = useState(false);
  const [useResponsibleUser, setUseResponsibleUser] = useState(false);
  const [users, setUsers] = useState([]);
  const [issueTouched, setIssueTouched] = useState(false);

  const {
    cancelButtonProps = {},
    confirmButtonProps = {},
    onCancel = () => {},
    onConfirm = () => {},
  } = props;

  const handleConfirm = () => {
    onClose();
    if (useIssueDescription) onConfirm(issueDescription);
    else if (useResponsibleUser) onConfirm(responsibleUser);
  }

  const handleCancel = () => {
    onClose();
    onCancel();
  }

  const displayDialog = ({ title, useIssueDescription, useResponsibleID }) => {
    setTitle(title);
    setUseIssueDescription(useIssueDescription);
    setUseResponsibleUser(useResponsibleID);
    setResponsibleUser("")
    setIssueTouched(false);

    onOpen();
  }

  const fetchUsers = async () => {
      try {
        const response = await notificationsService.listUsers();
        const userList = response?._embedded?.usuario_list || [];
  
        const users = userList.map(user => ({
          ...user,
          username: user.pessoa?.nome || user.login,
        }));
  
        setUsers(users);
      } catch (err) {
        showToast({ title: t(err), status: "error" });
      }
  };

  useEffect(() => {
      fetchUsers();
  }, []);

  useImperativeHandle(ref, () => ({
    displayDialog: displayDialog,
  }));

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={dialogRef}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>
            {useIssueDescription && (
              <FormControl isRequired isInvalid={ issueTouched && !issueDescription }>
                <FormLabel>
                    {t("alerts.dialog.insert-issue.label")}
                </FormLabel>
                <Textarea
                    value={issueDescription}
                    onChange={(e) => {
                      setIssueTouched(true);
                      setIssueDescription(e.currentTarget.value)
                    }}
                    placeholder={t("alerts.filters.placeholders.issue-reason")}
                    minH="120px"
                    resize="vertical"
                    whiteSpace="pre-wrap"
                />
                <FormErrorMessage>
                    {t("alerts.messages.errors.invalid-issue-reason")}
                </FormErrorMessage>
              </FormControl>
            )}

            {useResponsibleUser && (
              <FormControl>
                  <FormLabel>
                      {t("alerts.filters.fields.responsible")}
                  </FormLabel>
                      <SearchSelect
                          items={users}
                          name="users"
                          valueField="id"
                          displayField="username"
                          value={responsibleUser}
                          onChange={e => setResponsibleUser(e.currentTarget.value)}
                          placeholder={t("alerts.filters.placeholders.responsible")}
                      />
              </FormControl>
            )}

          </AlertDialogBody>

          <AlertDialogFooter>
            <HStack spacing="2">
              <Button {...cancelButtonProps} onClick={handleCancel}>
                {t("system.dialogs.delete.buttons.cancel")}
              </Button>

              <Button colorScheme="blue" {...confirmButtonProps} onClick={handleConfirm} disabled={!issueDescription && !responsibleUser}>
                {t("system.dialogs.delete.buttons.confirm")}
              </Button>
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
});

export default ConfirmDialogWithFormModal;
